import { useEffect, useState } from "react";
import { AgentChat } from "./AgentChat";
import { agentApi, type AgentThread } from "./agentApi";
import "./agent.css";

// "Ask Anders' assistant" — the right-side drawer on the Advisory workbench (M15).
// Mounted by the app shell on /app/advisory/*, so the M14 pages stay untouched.
// On a workbench page the conversation is tied to that advice (session context);
// on the advice desk it is a free conversation.

const ADVISORY_SESSION = /^\/app\/advisory\/(\d+)\/?$/;

export function AgentDrawerHost({ pathname }: { pathname: string }) {
  const [open, setOpen] = useState(false);
  if (!pathname.startsWith("/app/advisory")) return null;
  const m = pathname.match(ADVISORY_SESSION);
  const sessionId = m ? Number(m[1]) : undefined;
  return (
    <>
      {!open && (
        <button type="button" className="agent-launcher" onClick={() => setOpen(true)}>
          <span className="agent-launcher-mark" aria-hidden>
            A
          </span>
          Ask Anders' assistant
        </button>
      )}
      {open && <AgentDrawer key={sessionId ?? "desk"} sessionId={sessionId} onClose={() => setOpen(false)} />}
    </>
  );
}

function AgentDrawer({ sessionId, onClose }: { sessionId?: number; onClose: () => void }) {
  const [thread, setThread] = useState<AgentThread | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // Resume the latest conversation for this advice, or start one.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { threads } = await agentApi.threads(sessionId);
        const existing = sessionId ? threads[0] : undefined;
        const t = existing ?? (await startThread(sessionId, setNotice));
        if (!cancelled) setThread(t);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : "Could not open the assistant.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  const fresh = async () => {
    try {
      setThread(await startThread(sessionId, setNotice));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not start a conversation.");
    }
  };

  useEffect(() => {
    const onKey = (e: globalThis.KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <aside className="agent-drawer" aria-label="Anders' assistant">
      <header className="agent-drawer-head">
        <div>
          <span className="eyebrow">Assistant</span>
          <h2>Anders' assistant</h2>
          <p className="muted agent-drawer-sub">
            {thread?.investor_name ? `${thread.investor_name}${sessionId ? ` · advice #${sessionId}` : ""}` : "Prepares, never submits"}
          </p>
        </div>
        <div className="agent-drawer-actions">
          <button type="button" className="btn btn-ghost btn-sm" onClick={fresh}>
            New
          </button>
          <button type="button" className="btn btn-ghost btn-sm" onClick={onClose} aria-label="Close assistant">
            Close
          </button>
        </div>
      </header>
      {notice && <p className="agent-notice agent-pad">{notice}</p>}
      {error && <p className="agent-error agent-pad">{error}</p>}
      {thread && (
        <AgentChat
          key={thread.id}
          threadId={thread.id}
          onThread={setThread}
          currentSessionId={sessionId}
          suggestions={
            sessionId
              ? ["What's missing on this advice?", "Summarise the client's holdings and risk profile"]
              : ["Summarise Erik Holmberg's holdings and his open advice flows"]
          }
        />
      )}
      <footer className="agent-drawer-foot">AI-prepared drafts are reviewed and submitted by the advisor.</footer>
    </aside>
  );
}

export async function startThread(sessionId: number | undefined, onNotice?: (n: string | null) => void): Promise<AgentThread> {
  const { thread, fallbackReason } = await agentApi.create(sessionId ? { advisorySessionId: sessionId } : {});
  onNotice?.(fallbackReason ? `Demo mode: the scripted assistant is answering (${fallbackReason})` : null);
  return thread;
}
